// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Loader, Paper } from '@mantine/core';
import { getReferenceString, isOk } from '@medplum/core';
import type { OperationOutcome } from '@medplum/fhirtypes';
import { Document, OperationOutcomeAlert, useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { Location } from 'react-router';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { PageContainer } from '../../components/layout/PageContainer';
import { PatientVitalsSummary } from '../../components/patient/PatientVitalsSummary';
import { usePatient } from '../../hooks/usePatient';
import { usePatientTabBadges } from '../../hooks/usePatientTabBadges';
import { useRole } from '../../hooks/useRole';
import { PatientChartHeader } from './chart/PatientChartHeader';
import { usePatientListOutletContext } from './list/PatientListLayoutContext';
import classes from './PatientPage.module.css';
import type { PatientPageTabInfo } from './PatientPage.utils';
import { formatPatientPageTabUrl, getPatientPageTabs } from './PatientPage.utils';
import { PatientTabsNavigation } from './PatientTabsNavigation';

function getTabFromLocation(location: Location, tabs: PatientPageTabInfo[]): PatientPageTabInfo | undefined {
  const tabId = location.pathname.split('/')[3] ?? '';
  if (tabId.toLowerCase() === 'encounter') {
    return tabs.find((t) => t.id === 'appointment');
  }
  const tab = tabId
    ? tabs.find((t) => t.id === tabId || t.url.toLowerCase().startsWith(tabId.toLowerCase()))
    : undefined;
  return tab;
}

export function PatientPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const medplum = useMedplum();
  const membership = medplum.getProjectMembership();
  const { role } = useRole();
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const patient = usePatient({ setOutcome });
  const tabs = getPatientPageTabs(membership, role);
  const badges = usePatientTabBadges(patient);
  const { openSwitchPatient, isMobile } = usePatientListOutletContext();
  const [currentTab, setCurrentTab] = useState<string>(() => {
    return (getTabFromLocation(location, tabs) ?? tabs[0]).id;
  });

  const onTabChange = useCallback(
    (newTabName: string | null): void => {
      if (!patient?.id) {
        console.error('Not within a patient context');
        return;
      }
      const tab = newTabName ? tabs.find((t) => t.id === newTabName) : tabs[0];
      if (tab) {
        setCurrentTab(tab.id);
        navigate(formatPatientPageTabUrl(patient.id, tab))?.catch(console.error);
      }
    },
    [navigate, patient?.id, tabs]
  );

  useEffect(() => {
    const newTab = getTabFromLocation(location, tabs) ?? tabs[0];
    if (newTab.id !== currentTab) {
      setCurrentTab(newTab.id);
    }
  }, [currentTab, location, tabs]);

  if (outcome && !isOk(outcome)) {
    return (
      <Document>
        <OperationOutcomeAlert outcome={outcome} />
      </Document>
    );
  }

  const patientId = patient?.id;
  if (!patientId) {
    return (
      <Document>
        <Loader />
      </Document>
    );
  }

  return (
    <div key={getReferenceString(patient)} className={classes.chartRoot}>
      <PageContainer size="chart" fullHeight>
        <div className={classes.chartShell}>
          <Paper withBorder radius="md" shadow="sm">
            <PatientChartHeader patient={patient} onSwitchPatient={isMobile ? openSwitchPatient : undefined} />
          </Paper>
          {currentTab === 'profile' && role !== 'clinical-ops-rcm' && <PatientVitalsSummary patient={patient} />}
          <Paper withBorder radius="md" shadow="sm">
            <PatientTabsNavigation
              patient={patient}
              tabs={tabs}
              currentTab={currentTab}
              onTabChange={onTabChange}
              badges={badges}
            />
          </Paper>
        </div>
        <Outlet />
      </PageContainer>
    </div>
  );
}
